import React from 'react';
import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RelatedPages from '../../components/PublicSite/RelatedPages';
import { ACCOUNT_DELETION } from '../../utils/pageProse';

/**
 * Public Play / App Store account-deletion instructions (#103).
 * No auth required — reviewers and signed-out users must be able to read this page.
 */
const AccountDeletionPage = (): JSX.Element => (
  <Box sx={{ py: 4, px: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
    <Card sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
      <CardContent>
        <Typography variant="h3" component="h1" gutterBottom>
          Hesychia — Request account deletion
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Hesychia (ai.hesychia.chat) · hesychia.ai · AI ML Operations, LLC
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ACCOUNT_DELETION.intro}
        </Typography>
      </CardContent>

      <Divider />

      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              How to delete your account
            </Typography>
            <Typography variant="body1" component="ol" sx={{ pl: 2.5, m: 0 }}>
              <li>
                Open <strong>Hesychia</strong> in the mobile app or at{' '}
                <a href="https://hesychia.ai" rel="noopener noreferrer">
                  hesychia.ai
                </a>{' '}
                for your account.
              </li>
              <li>{ACCOUNT_DELETION.signIn}</li>
              <li>{ACCOUNT_DELETION.goToAccount}</li>
              <li>{ACCOUNT_DELETION.confirm}</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              What happens when you delete
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              Deactivated immediately
            </Typography>
            <Typography variant="body1" component="ul" sx={{ pl: 2.5, m: 0 }}>
              <li>{ACCOUNT_DELETION.deactivated}</li>
              <li>{ACCOUNT_DELETION.loseAccess}</li>
              <li>{ACCOUNT_DELETION.sessions}</li>
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
              Data retained after soft-delete
            </Typography>
            <Typography variant="body1" paragraph>
              {ACCOUNT_DELETION.softDelete} {ACCOUNT_DELETION.remain} {ACCOUNT_DELETION.includes}{' '}
              {ACCOUNT_DELETION.purposes}
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom>
              Retention period
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 0 }}>
              {ACCOUNT_DELETION.retention} {ACCOUNT_DELETION.request} {ACCOUNT_DELETION.noClaim}
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Cannot access your account?
            </Typography>
            <Typography variant="body1" paragraph>
              If you cannot sign in to complete in-app deletion, contact AI ML Operations, LLC via{' '}
              <a href="https://www.aimloperations.com" rel="noopener noreferrer">
                aimloperations.com
              </a>
              . {ACCOUNT_DELETION.email} {ACCOUNT_DELETION.days}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ACCOUNT_DELETION.staff}
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      <Divider />

      <CardContent>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Button component={RouterLink} to="/signin/" size="small">
            Sign in
          </Button>{' '}
          ·{' '}
          <Button component={RouterLink} to="/account/" size="small">
            Account
          </Button>{' '}
          ·{' '}
          <Button component={RouterLink} to="/terms_of_service/" size="small">
            Terms of Service
          </Button>
        </Typography>
      </CardContent>
    </Card>
    <RelatedPages />
  </Box>
);

export default AccountDeletionPage;
